import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosError } from 'axios'
import { useLocaleStore } from '@/stores/locale'

class HttpClient {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor - добавить locale header
    this.instance.interceptors.request.use(
      config => {
        const localeStore = useLocaleStore()
        config.headers['user-locale'] = localeStore.currentLocale
        return config
      },
      error => Promise.reject(error)
    )

    // Response interceptor - обработка ошибок
    this.instance.interceptors.response.use(
      response => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // TODO: Обработка unauthorized (редирект на логин, если нужно)
          console.error('Unauthorized request')
        }

        if (error.response?.status === 500) {
          console.error('Server error:', error.response.data)
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * GET запрос
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(url, config)
    return response.data
  }

  /**
   * POST запрос
   */
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(url, data, config)
    return response.data
  }

  /**
   * PUT запрос
   */
  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(url, data, config)
    return response.data
  }

  /**
   * DELETE запрос
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(url, config)
    return response.data
  }

  /**
   * Получить raw Axios instance (для специальных случаев)
   */
  getInstance(): AxiosInstance {
    return this.instance
  }
}

// Singleton instance
export const httpClient = new HttpClient()
